'use client';

import { HeaderStore } from 'components/pcc-components';
import { useTranslations } from 'next-intl';
import { useStoreInfoDrawer } from './store-info-drawer/store-info-drawer-context';
import { useDealerLocatorDrawer } from 'components/dealer-locator/dealer-locator-drawer-context';
import { useState, useLayoutEffect, useEffect, useTransition } from 'react';
import { returnFocusToNavItem } from '../../utils/common-utils';
import { selectDefaultStore } from 'services/dealer-locator/actions';
import LoaderWithOverlay from '../../../account/(with-nav)/control-center/common/LoaderWithOverlay';
import { useStoreLocatorTooltip } from '../../hooks/use-store-locator-tooltip';
import { usePathname, useSearchParams } from 'next/navigation';

export interface StoreItemProps {
  hasStoreSelected?: boolean;
  storeName?: string;
  storeLocation?: string;
  isMobile?: boolean;
  assignDefaultStore?: boolean;
  isGuestUserOnCatcorp?: boolean;
  isMobileSearch?: boolean;
}

export const StoreClient = ({
  hasStoreSelected = false,
  storeName,
  storeLocation,
  isMobile = false,
  assignDefaultStore,
  isGuestUserOnCatcorp,
  isMobileSearch
}: StoreItemProps) => {
  const t = useTranslations();
  const [isPending, startTransition] = useTransition();
  const label = t('CAT_SELECT_STORE');
  const storeIconID = isMobile ? 'selectStoreMobile' : 'selectStore';
  const storeInfoDrawer = useStoreInfoDrawer();
  const dealerLocatorDrawer = useDealerLocatorDrawer();
  const drawerControl = hasStoreSelected
    ? storeInfoDrawer
    : dealerLocatorDrawer;
  const [isOpenFromNavItem, setIsOpenFromNavItem] = useState(false);
  const queryParams = useSearchParams();
  const pathname = usePathname();
  const { handleToolTip, shouldDisplayTooltip } = useStoreLocatorTooltip(
    isGuestUserOnCatcorp,
    isMobile
  );
  const hasQueryParms = !!queryParams.size;
  const allParams = queryParams.toString();

  const handleOpen = () => {
    drawerControl?.openDrawer();
    setIsOpenFromNavItem(true);
  };

  useEffect(() => {
    const callSelectDefaultStore = async () => {
      if (assignDefaultStore) {
        startTransition(
          async () =>
            await selectDefaultStore(hasQueryParms, pathname, allParams)
        );
      }
    };
    callSelectDefaultStore();
  }, [allParams, assignDefaultStore, hasQueryParms, pathname]);

  useLayoutEffect(() => {
    if (!drawerControl?.isDrawerOpen && isOpenFromNavItem) {
      setIsOpenFromNavItem(false);
      returnFocusToNavItem(`#${storeIconID}`);
    }
  }, [drawerControl?.isDrawerOpen, isOpenFromNavItem, storeIconID]);

  return (
    <>
      <HeaderStore
        id={storeIconID}
        label={hasStoreSelected ? storeName : label}
        subLabel={storeLocation}
        handleDrawerProps={drawerControl?.initiatorProps}
        action={handleOpen}
        isCurrent={isOpenFromNavItem}
        isMobile={isMobile}
        storeLocatorMessage={t('ALP_DEALER_SECTION_DESCRIPTION')}
        isTooltipActive={shouldDisplayTooltip}
        handleToolTip={handleToolTip}
        isMobileSearch={isMobileSearch}
      />
      {isPending && (
        <LoaderWithOverlay
          message={t('SALESFORCE_CHAT_LOADING_TEXT')}
          cardClassName="px-2 py-2 align-items-center cat-u-theme-typography-label"
          messageClassName="ps-2"
        />
      )}
    </>
  );
};
